//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: ESU Data tab
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                  Initial Revision.
//    002   07.09.23 Sean Flook                  Cleaned the code.
//    003   06.10.23 Sean Flook                  Use colour variables.
//    004   27.10.23 Sean Flook                  Use new dataFormStyle.
//    005   24.11.23 Sean Flook                  Moved Box to @mui/system.
//    006   02.01.24 Sean Flook                  Changed console.log to console.error for error messages.
//    007   05.01.24 Sean Flook                  Changes to sort out warnings and use CSS shortcuts.
//    008   25.01.24 Sean Flook                  Correctly handle status code 204.
//    009   08.03.24 Joshua McCormick  IMANN-280 Adding Padding bottom to match padding top in the stack/chips
//    010   11.03.24 Sean Flook            GLB12 Adjusted height to remove gap.
//    011   15.03.24 Sean Flook             GLB6 Use individual buttons to toggle between updates and edits.
//    012   22.03.24 Sean Flook            GLB12 Changed to use dataFormStyle so height can be correctly set.
//    013   19.06.24 Sean Flook        IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//    014   10.09.24 Sean Flook        IMANN-980 Only write to the console if the user has the showMessages right.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    015   30.01.25 Sean Flook       IMANN-1673 Changes required for new user settings API.
//    016   23.04.25 Sean Flook       IMANN-1768 Changes required for selecting properties in property and street forms.
//endregion Version 1.0.5.0
//region Version 1.0.7.0
//    017   13.06.25 Sean Flook       IMANN-1812 Moved controls from @mui/system to @mui/material.
//    018   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    019   17.12.25 Sean Flook       IMANN-2196 Removed the information component as no longer required in this control.
//endregion Version 1.0.7.0
//region Version 1.0.7.1
//    020   27.04.26 Sean Flook       IMANN-2411 Correctly compare UPRNs.
//endregion Version 1.0.7.1
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useState, useEffect, Fragment } from "react";

import PropertyContext from "../context/propertyContext";
import StreetContext from "../context/streetContext";
import UserContext from "../context/userContext";
import SettingsContext from "../context/settingsContext";

import { GetPropertyHistoryByUPRNUrl, GetStreetHistoryByUSRNUrl } from "../configuration/ADSConfig";

import { Button, Skeleton, Stack, Typography, Box } from "@mui/material";
import ADSEntityHistoryList from "../components/ADSEntityHistoryList";

import UpdateIcon from "@mui/icons-material/Update";
import EditIcon from "@mui/icons-material/Edit";

import { blueButtonStyle, dataFormStyle, greyButtonStyle, toolbarStyle } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";

function EntityHistoryTab({ variant }) {
  const theme = useTheme();

  const streetContext = useContext(StreetContext);
  const propertyContext = useContext(PropertyContext);
  const userContext = useContext(UserContext);
  const settingsContext = useContext(SettingsContext);

  const [apiUrl, setApiUrl] = useState(null);
  const [data, setData] = useState();
  const [dataUsrn, setDataUsrn] = useState(streetContext.currentStreet.usrn);
  const [dataUprn, setDataUprn] = useState(propertyContext.currentProperty.uprn);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState("update");

  /**
   * Event to handle updating the history data.
   */
  const handleUpdateHistory = () => {
    if (currentTab !== "update") setCurrentTab("update");
    setData({}); // clear the display
    setLoading(true); // make render ignore null data (which would crash it)
    setData(null); // useEffect will fetch data then setLoading(false) so render will display new data
  };

  /**
   * Event to handle viewing the edits in progress data.
   */
  const handleEditsInProgress = () => {
    if (currentTab !== "edits") setCurrentTab("edits");
  };

  useEffect(() => {
    async function SetUpEntityHistory() {
      const changedParent =
        variant === "street"
          ? streetContext.currentStreet.usrn !== dataUsrn
          : Number(propertyContext.currentProperty.uprn) !== Number(dataUprn) ||
            Number(propertyContext.currentProperty.usrn) !== Number(dataUsrn);
      if (!data || changedParent) {
        if (apiUrl) {
          setLoading(true);
          const fetchUrl =
            variant === "street"
              ? `${apiUrl.url}/${streetContext.currentStreet.usrn}`
              : propertyContext.currentProperty.uprn > 0
                ? `${apiUrl.url}/${propertyContext.currentProperty.uprn}`
                : `${apiUrl.url}/${propertyContext.currentProperty.usrn}`;
          fetch(fetchUrl, {
            headers: apiUrl.headers,
            crossDomain: true,
            method: "GET",
          })
            .then((res) => (res.ok ? res : Promise.reject(res)))
            .then((res) => {
              if (res.status && res.status === 204) return [];
              else return res.json();
            })
            .then(
              (result) => {
                setData(result);
              },
              (error) => {
                if (error.status && error.status === 401) {
                  userContext.onExpired();
                } else {
                  if (userContext.currentUser.showMessages) console.error(`[ERROR] Get ${variant} history`, error);
                }
              }
            )
            .then(() => {
              if (variant === "street") setDataUsrn(streetContext.currentStreet.usrn);
              else {
                setDataUprn(propertyContext.currentProperty.uprn);
                setDataUsrn(propertyContext.currentProperty.usrn);
              }
              setLoading(false);
            });
        } else {
          if (userContext.currentUser.showMessages) console.error("[ERROR] Property history apiUrl is null");
        }
      }
    }

    if (!apiUrl) {
      if (variant === "street") {
        setApiUrl(GetStreetHistoryByUSRNUrl(userContext.currentUser));
      } else {
        setApiUrl(GetPropertyHistoryByUPRNUrl(userContext.currentUser));
      }
    }

    if (apiUrl && (!data || Number(propertyContext.currentProperty.uprn) !== Number(dataUprn))) SetUpEntityHistory();

    return () => {};
  }, [variant, streetContext, propertyContext, userContext, settingsContext, apiUrl, data, dataUsrn, dataUprn]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-history-tab">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          spacing={1}
          sx={{ pl: theme.spacing(2), pt: theme.spacing(0.55) }}
        >
          <Button
            autoFocus
            variant="contained"
            startIcon={<UpdateIcon />}
            onClick={handleUpdateHistory}
            sx={currentTab === "update" ? blueButtonStyle : greyButtonStyle}
          >
            <Typography variant="caption">Update history</Typography>
          </Button>
          <Button
            disabled
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditsInProgress}
            sx={currentTab === "edits" ? blueButtonStyle : greyButtonStyle}
          >
            <Typography variant="caption">Edits in progress</Typography>
          </Button>
        </Stack>
      </Box>
      <Box sx={dataFormStyle("EntityHistoryTab")}>
        {loading ? (
          <Skeleton variant="rectangular" height="30px" width="100%" />
        ) : (
          <ADSEntityHistoryList historySummaryData={data} />
        )}
        <Box sx={{ height: "24px" }} />
      </Box>
    </Fragment>
  );
}

export default EntityHistoryTab;
